import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MotiView } from 'moti';
import {
  forwardRef,
  memo,
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useImperativeHandle,
} from 'react';
import {
  ActivityIndicator,
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import Animated, {
  interpolate,
  SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { DefaultTheme, useTheme } from 'styled-components';

const UP = 1;
const DOWN = -1;
const kThreshold = 80;

type Props = {
  scrollY?: SharedValue<number>;
  containerStyle?: StyleProp<ViewStyle>;
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  inline?: boolean;
};

type Variant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'warning'
  | 'success'
  | 'info'
  | 'white'
  | 'default';
type Size = 'normal' | 'large' | 'small';

export const ButtonColors: {
  [p in Variant]: {
    bgColor: (theme: DefaultTheme) => string;
    textColor: (theme: DefaultTheme) => string;
    borderedBgColor?: (theme: DefaultTheme) => string;
    borderedTextColor?: (theme: DefaultTheme) => string;
  };
} = {
  primary: {
    bgColor: p => p.link,
    textColor: p => p.actionButton.textColorLight,
  },
  secondary: {
    bgColor: p => p.colors.secondary,
    textColor: p => p.actionButton.textColorLight,
  },
  danger: {
    bgColor: p => p.colors.status.danger,
    textColor: p => p.actionButton.textColorLight,
  },
  warning: {
    bgColor: p => p.colors.status.warn,
    textColor: p => p.actionButton.textColorLight,
  },
  success: {
    bgColor: p => p.colors.status.success,
    textColor: p => p.actionButton.textColorLight,
  },
  info: {
    bgColor: p => p.colors.status.info,
    textColor: p => p.actionButton.textColorLight,
  },
  default: {
    bgColor: p => p.actionButton.defaultBgColor,
    textColor: p => p.actionButton.defaultTextColor,
  },
  white: {
    bgColor: p => p.actionButton.whiteBgColor,
    textColor: p => p.actionButton.whiteTextColor,
    borderedBgColor: () => 'transparent',
  },
};

const useButtonMetrics = (): {
  [p in Size]: { padding: number; height: number; fontSize: number };
} => {
  const { rpx } = useResponsiveContext();
  return {
    normal: { height: rpx(56), padding: rpx(32), fontSize: rpx(20) },
    large: { height: rpx(78), padding: rpx(44), fontSize: rpx(32) },
    small: { height: rpx(44), padding: rpx(24), fontSize: rpx(18) },
  };
};

export const useButtonColor = (variant: Variant) => {
  const theme = useTheme();
  return ButtonColors[variant].bgColor(theme);
};

export type ActionButtonProps = {
  text: string;
  textStyle?: StyleProp<TextStyle>;
  style?: StyleProp<ViewStyle>;
  variant?: Variant;
  size?: Size;
  disabled?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  bordered?: boolean;
  buttonRef?: RefObject<TouchableOpacity>;
  disabledStyle?: StyleProp<ViewStyle>;
  onPress?: () => void;
};

export type BottomActionType = {
  (props: Props, context?: any): ReactElement<any, any> | null;
  Button: typeof ActionButton;
};

export type BottomActionRef = {
  reset: (offset: number) => void;
};

export const BottomAction: BottomActionType = memo(
  forwardRef<BottomActionRef, Props>(
    ({ containerStyle, children, style, inline, ...props }, ref) => {
      const insets = useSafeAreaInsets();
      const { rpx } = useResponsiveContext();
      const hidden = useSharedValue(0);
      const direction = useSharedValue(0);
      const lastOffset = useSharedValue(0);
      const fallbackScrollY = useSharedValue(0);
      const scrollY = props.scrollY ?? fallbackScrollY;
      const maxOffset = rpx(100);
      const minOffset = rpx(10);
      const extent = rpx(56 + 32);

      const reset = useCallback(
        (offset: number) => {
          scrollY.value = offset;
          lastOffset.value = offset;
          direction.value = 0;
          hidden.value = 0;
        },
        [direction, hidden, lastOffset, scrollY],
      );

      useImperativeHandle(ref, () => ({
        reset,
      }));

      useAnimatedReaction(
        () => scrollY.value,
        (curr, prev) => {
          if (prev == null) return;
          const dir = curr > prev ? UP : DOWN;
          if (dir !== direction.value) {
            lastOffset.value = curr;
            direction.value = dir;
          }
          const diff = Math.abs(lastOffset.value - curr);
          if (
            curr > maxOffset &&
            curr > prev &&
            hidden.value === 0 &&
            (diff > kThreshold || curr <= minOffset)
          ) {
            // scroll up
            hidden.value = withTiming(1, { duration: 250 });
          } else if (curr < prev && diff > kThreshold) {
            // scroll down
            hidden.value = withTiming(0, { duration: 250 });
          }
        },
      );

      const animatedStyle = useAnimatedStyle(() => {
        return inline
          ? {}
          : {
              opacity: interpolate(hidden.value, [0, 1], [1, 0]),
              transform: [
                {
                  translateY: interpolate(
                    hidden.value,
                    [0, 1],
                    [0, insets.bottom + extent],
                  ),
                },
              ],
            };
      });

      return (
        <MotiView
          from={{ opacity: 0, translateY: inline ? 0 : maxOffset }}
          animate={{ opacity: 1, translateY: 0 }}
          pointerEvents={'box-none'}
          style={[
            inline
              ? undefined
              : {
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: Math.max(insets.bottom, rpx(15)),
                },
            style,
          ]}
        >
          <Animated.View
            css={`
              flex-direction: row;
              justify-content: center;
              align-items: center;
            `}
            style={[containerStyle, animatedStyle]}
            pointerEvents={'box-none'}
          >
            {children}
          </Animated.View>
        </MotiView>
      );
    },
  ),
) as any;

const ActionButton = memo(
  ({
    text,
    textStyle,
    icon,
    iconRight,
    style,
    variant = 'primary',
    size = 'normal',
    disabled,
    disabledStyle,
    loading,
    bordered,
    buttonRef,
    onPress,
  }: ActionButtonProps) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const colorFactory = ButtonColors[variant];
    const { height, padding, fontSize } = useButtonMetrics()[size];
    const bgColor = colorFactory.bgColor(theme);
    const textColor = colorFactory.textColor(theme);
    const borderedBgColor = colorFactory.borderedBgColor?.(theme);
    return (
      <TouchableOpacity
        css={`
          height: ${height}px;
          padding: 0 ${padding}px;
          border-radius: ${height / 2}px;
          background-color: ${bordered ? borderedBgColor ?? bgColor : bgColor};
          border-width: ${bordered ? 1 : 0}px;
          border-color: ${bordered ? bgColor : 'transparent'};
          flex-direction: row;
          justify-content: center;
          align-items: center;
          box-shadow: 0 0 ${rpx(5)}px rgba(0, 0, 0, 0.5);
          elevation: 3;
          opacity: ${disabled ? 0.75 : 1};
        `}
        disabled={disabled}
        onPress={onPress}
        style={[style, disabled ? disabledStyle : undefined]}
        ref={buttonRef}
      >
        {loading && (
          <ActivityIndicator
            color={textColor}
            size={size === 'large' ? 'large' : 'small'}
            css={`
              margin-right: ${rpx(5)}px;
            `}
          />
        )}
        {icon}
        <Text
          css={`
            font-family: ${FontFamily.NotoSans.Regular};
            font-size: ${fontSize}px;
            text-align: center;
            color: ${textColor};
          `}
          style={textStyle}
        >
          {text}
        </Text>
        {iconRight}
      </TouchableOpacity>
    );
  },
);

BottomAction.Button = ActionButton;
